import { NodeGraphPropsBase, NodeGraphSpec } from '@/core';
import { PropToSpecMapping, UseDataQueryResult } from '../../../plugin-system';
import { DependencyLink } from './dependency-map/types';

export const NODE_GRAPH_DATA_TYPE = 'SingleValue';

export function createInitialNodeGraphOptions(): NodeGraphSpec {
  return {
    unit: { kind: 'Decimal' },
  };
}

/**
 * Maps camelCased panel props back to their snake_cased spec paths. The
 * panel infra ships a default identity mapping, which works for single-word
 * fields like `unit` / `thresholds` but silently drops fields whose names
 * actually differ between spec (`from_label`) and props (`fromLabel`).
 */
export const NODE_GRAPH_PROP_TO_SPEC_MAPPING: PropToSpecMapping<NodeGraphPropsBase, NodeGraphSpec> = {
  unit: 'unit',
  thresholds: 'thresholds',
  fromLabel: 'from_label',
  toLabel: 'to_label',
};

export interface NodeGraphLinksResult {
  links: DependencyLink[];
  /**
   * Per-node self-traffic value (`from === to` series). Surfaced via a node
   * tooltip rather than a link so the layout stays a clean DAG and the node
   * color isn't influenced by traffic with no visible edge.
   */
  selfLoops: Map<string, number>;
  /** Set when the query shape is incompatible (e.g. wrong number of labels). */
  error?: string;
}

export interface ExtractLinksOptions {
  /**
   * Series label whose value names the source endpoint. When unset (along
   * with `toLabel`), falls back to the alphabetically-first of the two
   * grouping labels — matches how Prometheus serializes labels and works
   * out of the box for pairs like `parent_service` / `service`.
   */
  fromLabel?: string;
  /** Source-of-target counterpart to `fromLabel`; same fallback rules. */
  toLabel?: string;
}

/**
 * Convert `sum by(label_a, label_b)` query results into `links` (plus
 * `selfLoops` for `from === to` series; see field doc).
 *
 * Edge direction:
 *  - If both `fromLabel` and `toLabel` are provided, labels are read by
 *    name and series that don't carry both are skipped.
 *  - Otherwise (the default), the two grouping labels are read in
 *    alphabetical order. This is what Prometheus actually returns on the
 *    wire (Go's `encoding/json` sorts map keys); explicit names exist as
 *    an escape hatch when the alphabetical order doesn't match the
 *    intended source-to-target direction.
 *
 * Series with the wrong label count are skipped silently; `error` is only
 * set when nothing valid extracts.
 */
export function extractLinksFromQueryResults(
  queryResults: UseDataQueryResult<'SingleValue'>['queryResults'],
  { fromLabel, toLabel }: ExtractLinksOptions = {}
): NodeGraphLinksResult {
  const links: DependencyLink[] = [];
  const selfLoops = new Map<string, number>();
  let badLabelCount: number | undefined;
  let sawMissingExplicitLabel = false;

  // Both-or-nothing: a partial config falls back to alphabetical so the
  // user doesn't see a half-broken state mid-edit (e.g. typed `from_label`
  // but hasn't picked `to_label` yet).
  const useExplicitLabels = fromLabel !== undefined && fromLabel !== '' && toLabel !== undefined && toLabel !== '';

  queryResults.forEach((result) => {
    // Don't gate on isFetching because that would cause a flash of "No data":
    if (result.isLoading || result.data === undefined) return;

    result.data.values.forEach((series) => {
      if (typeof series.value !== 'number') return;

      const labels = series.labels ?? {};
      const labelKeys = Object.keys(labels);

      if (labelKeys.length !== 2) {
        if (badLabelCount === undefined) badLabelCount = labelKeys.length;
        return;
      }

      let from: string | undefined;
      let to: string | undefined;
      if (useExplicitLabels) {
        from = labels[fromLabel];
        to = labels[toLabel];
        if (from === undefined || to === undefined) {
          sawMissingExplicitLabel = true;
          return;
        }
      } else {
        // Object.keys preserves JSON insertion order, which for Prometheus is
        // alphabetical (Go `encoding/json` sorts map keys). Sorting here
        // keeps the assignment stable for non-Prometheus data sources too.
        const [fromKey, toKey] = [...labelKeys].sort() as [string, string];
        from = labels[fromKey];
        to = labels[toKey];
        if (from === undefined || to === undefined) return;
      }

      if (from === to) {
        // Drop zero-valued self-edges: they're indistinguishable from "no
        // self-traffic" and would render an empty-looking tooltip.
        if (series.value === 0) return;

        // Last-write-wins across duplicate self-edges (e.g. compare-mode);
        // the underlying prom query produces one self-edge per node so this
        // mainly affects multi-result mixing rather than within a result.
        selfLoops.set(from, series.value);
        return;
      }

      links.push({ from, to, value: series.value });
    });
  });

  if (links.length === 0 && selfLoops.size === 0) {
    if (badLabelCount !== undefined) {
      return {
        links: [],
        selfLoops,
        error: `Node graph expects exactly 2 grouping labels (e.g. \`sum by(label_a, label_b)\`), got ${badLabelCount}.`,
      };
    }

    // Explicit names that don't match anything in the data is almost always
    // a typo in the editor — call it out specifically rather than silently
    // showing "no data."
    if (useExplicitLabels && sawMissingExplicitLabel) {
      return {
        links: [],
        selfLoops,
        error: `Node graph couldn't find labels "${fromLabel}" and "${toLabel}" on the query results. Check the From / To label names in the panel settings.`,
      };
    }
  }

  return { links, selfLoops };
}
